/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { isStyleVisible } from "common/utils/ui/element";
import { chunkArray } from "common/utils/array";
import { isElementVisible } from "common/utils/ui/visibility";
import { productItemsPluralizer } from "common/product-items-pluralizer";
import initSameDayBadge from "components/jiffy/same-day-badge/same-day-badge";
import CompositionEstimatesClass from "./composition-estimates-class";

const SELECTORS = {
  DELIVERY_CONTAINER: ".js-plp-dates-container",
  DELIVERY_DATE: ".js-plp-delivery-date",
  DELIVERY_DAY: ".js-plp-delivery-day",
  DELIVERY_FLASH_LOADER: ".js-plp-delivery-loader",
  DELIVERY_INVENTORY: ".js-plp-delivery-inventory",
  DELIVERY_SHIPPING_RATE: ".js-plp-delivery-shipping-rate",
  DELIVERY_SPINNER: ".js-plp-delivery-spinner",
  DELIVERY_TIMELEFT: ".js-plp-delivery-timeleft",
  SAME_DAY_ITEM: ".js-same-day-item",
  SAME_DAY_BADGES: ".js-same-day-item[data-same-day-supported='true']",
  DELIVERY_LOADING: ".js-plp-delivery-loading",
  DELIVERY_LOADED: ".js-plp-delivery-loaded"
};

const SAME_DAY_DELIVERY_DATE = "Today, by 9 PM";

const fetcher = new CompositionEstimatesClass();

// Global variable is used to sync different js chunks that call this module
const compositionIdsSent = window?.jiffy?.ui?.productsCompositionIdsSent || [];

// Track ongoing requests to prevent duplicates
const ongoingRequests = new Map();

// Helper function to create a unique request key
const createRequestKey = (compositionId, options) => {
  const isMade = Boolean(options?.isMade);
  const isFlash = Boolean(options?.isFlash);
  const isSameDayEstimates = Boolean(options?.isSameDayEstimates);
  const isProductCardV3 = Boolean(options?.isProductCardV3);

  return `${compositionId}-${isMade}-${isFlash}-${isSameDayEstimates}-${isProductCardV3}`;
};

// Helper function to check if a composition ID should be processed
const shouldProcessCompositionId = (compositionId, options) => {
  const requestKey = createRequestKey(compositionId, options);

  // Check if there's already an ongoing request
  if (ongoingRequests.has(requestKey)) {
    return false;
  }

  // For regular compositions, also check the global sent list
  const isMade = Boolean(options?.isMade);
  const isFlash = Boolean(options?.isFlash);
  if (!isMade && !isFlash && compositionIdsSent.includes(compositionId)) {
    return false;
  }

  return true;
};

// Helper function to mark a composition ID as being processed
const markCompositionIdAsProcessing = (compositionId, options) => {
  const requestKey = createRequestKey(compositionId, options);
  const isMade = Boolean(options?.isMade);
  const isFlash = Boolean(options?.isFlash);

  // Add to ongoing requests
  ongoingRequests.set(requestKey, true);

  // Add to global sent list for regular compositions
  if (!isMade && !isFlash) {
    compositionIdsSent.push(compositionId);
  }
};

// Helper function to mark a composition ID as completed
const markCompositionIdAsCompleted = (compositionId, options) => {
  const requestKey = createRequestKey(compositionId, options);
  ongoingRequests.delete(requestKey);
};

/* eslint-disable no-unused-vars */
const trackDeliveryDatesRendered = (renderTime, ajaxTime, additionalTags = {}) => {
  // track("delivery_dates_rendered", {
  //   page: window?.jiffy?.ui?.productsRenderPage || "other",
  //   time_to_get_date: Math.floor(ajaxTime),
  //   time_to_render_date: Math.floor(renderTime),
  //   ...additionalTags
  // });
  // TODO: cleanup code related to delivery_dates_rendered calculations
  // Cart: https://github.com/sdtechdev/spree-jiffyshirts/pull/33466 , https://github.com/sdtechdev/spree-jiffyshirts/pull/33581
  // PDP: https://github.com/sdtechdev/spree-jiffyshirts/pull/33379
  // PLP: https://github.com/sdtechdev/spree-jiffyshirts/pull/33623
};

const isSpinnerHidden = $el => {
  const $spinner = $el.querySelector(".js-plp-delivery-spinner");

  // Need the negated result
  return !isStyleVisible($spinner);
};

const getCompositionIds = rows =>
  Array.from(rows)
    .filter(cell => cell.dataset.assignable === "true")
    .reduce(
      (acct, element) => {
        const { compositionId: id, sameDayCutoff, made, flash } = element.dataset;

        // Remove the old deduplication logic since we handle it in loadCompositionEstimates
        if (flash === "true") {
          acct.flashCompositionIds.push(id);
        } else if (sameDayCutoff) {
          acct.sameDayCutoffIds.push(id);
        } else if (made === "true") {
          acct.madeCompositionIds.push(id);
        } else {
          acct.regularCompositionIds.push(id);
        }
        return acct;
      },
      {
        regularCompositionIds: [],
        sameDayCutoffIds: [],
        madeCompositionIds: [],
        flashCompositionIds: []
      }
    );

const renderSameDayEstimates = (compositionId, flashCutOffTime, deliveryDate) => {
  const $elements = document.querySelectorAll(
    `${SELECTORS.SAME_DAY_ITEM}[data-composition-id="${compositionId}"]`
  );

  if ($elements.length === 0) {
    return;
  }

  const isSameDay = deliveryDate && deliveryDate.toLowerCase().includes("today");
  const isNextDay = deliveryDate && deliveryDate.toLowerCase().includes("tomorrow");

  $elements.forEach($element => {
    // Hide spinner - this was the missing piece for same-day estimates
    const $spinner = $element.querySelector(SELECTORS.DELIVERY_SPINNER);
    if ($spinner) {
      $spinner.classList.add("hidden");
    }

    // Also check for spinners in related delivery containers
    const $deliveryContainer = document.querySelector(
      `.product-card__delivery-row[data-composition-id="${compositionId}"]`
    );
    if ($deliveryContainer) {
      const $containerSpinner = $deliveryContainer.querySelector(SELECTORS.DELIVERY_SPINNER);
      if ($containerSpinner) {
        $containerSpinner.classList.add("hidden");
      }

      if (deliveryDate) {
        // Show delivery date in the container
        const $deliveryDateEl = $deliveryContainer.querySelector(SELECTORS.DELIVERY_DATE);
        if ($deliveryDateEl) {
          $deliveryDateEl.setAttribute("data-date", deliveryDate);
          $deliveryDateEl.setAttribute("data-timeleft", flashCutOffTime);
          $deliveryDateEl.innerHTML = deliveryDate;
          // Add tomorrow class for same-day/next-day deliveries
          $deliveryDateEl.classList.toggle(
            "product-card__delivery-date-cart--tomorrow",
            isSameDay || isNextDay
          );
        }
        const $deliveryContainerEl = $deliveryContainer.querySelector(SELECTORS.DELIVERY_CONTAINER);
        if ($deliveryContainerEl) {
          $deliveryContainerEl.classList.remove("hidden");
        }

        // Add same-day badge if it's same day or next day
        if (isSameDay || isNextDay) {
          renderSameDayBadge($deliveryContainer, flashCutOffTime, deliveryDate);
        }
      } else {
        $deliveryContainer.dataset.assignable = false;
      }
    }

    if (deliveryDate) {
      const badge = initSameDayBadge($element, {
        pageType: "header",
        flashCutOffTime,
        deliveryDate
      });

      badge.toggleBadge(true);
    }
  });
};

const renderSameDayBadge = ($container, flashCutOffTime, deliveryDate) => {
  const $productCard = $container.parentElement;

  const badge = initSameDayBadge($productCard, {
    pageType: "plp",
    flashCutOffTime,
    deliveryDate
  });

  if (badge) {
    badge.toggleBadge(true);
  }
};

const renderEstimates = ({
  compositionId,
  date,
  deliveryType,
  inventory,
  oneShipDay,
  orderInTheNext,
  shippingRate,
  timeleft,
  deliveryDay,
  isMade
}) => {
  const isSameDay = date.toLowerCase().includes("today");
  const isNextDay = date.toLowerCase().includes("tomorrow");

  const renderDeliveryEstimates = $container => {
    if (!date) {
      $container.querySelector(SELECTORS.DELIVERY_SPINNER).classList.add("hidden");
      $container.dataset.assignable = false;
      return;
    }

    $container
      .querySelector(SELECTORS.DELIVERY_DATE)
      .classList.toggle("product-card__delivery-date-cart--tomorrow", !!oneShipDay);

    $container.querySelector(SELECTORS.DELIVERY_DATE).setAttribute("data-date", date);
    $container.querySelector(SELECTORS.DELIVERY_DATE).setAttribute("data-timeleft", timeleft);
    $container.querySelector(SELECTORS.DELIVERY_DATE).innerHTML = date;
    $container.querySelector(SELECTORS.DELIVERY_CONTAINER).classList.remove("hidden");
    $container.querySelector(SELECTORS.DELIVERY_SPINNER).classList.add("hidden");

    const $sameDay = $container.closest(".js-product-card")?.previousElementSibling;
    const $timeleft = $sameDay?.querySelector(SELECTORS.DELIVERY_TIMELEFT);

    if (orderInTheNext && $timeleft) {
      const timeLeftText = orderInTheNext.replace(" hours", "h").replace(" minutes", "m");
      $timeleft.dataset.countdown = timeleft;
      $timeleft.textContent = timeLeftText;
      $timeleft.parentElement.classList.remove("hidden");

      const $loader = $sameDay.querySelector(SELECTORS.DELIVERY_FLASH_LOADER);
      $loader?.classList.add("hidden");

      const $deliveryDay = $sameDay.querySelector(SELECTORS.DELIVERY_DAY);
      if ($deliveryDay) {
        $deliveryDay.textContent = deliveryDay;
        $deliveryDay.parentElement.classList.remove("hidden");
      }

      const $shipingRate = $sameDay?.querySelector(SELECTORS.DELIVERY_SHIPPING_RATE);
      if ($shipingRate) {
        $shipingRate.textContent = shippingRate;
        $shipingRate.parentElement.classList.remove("hidden");
      }
    }

    if (inventory) {
      const $inventory = $container.querySelector(SELECTORS.DELIVERY_INVENTORY);
      const { taxon } = $inventory.dataset;
      const productItems = productItemsPluralizer(inventory, taxon);
      $inventory.textContent = `Only ${productItems} left for ${deliveryDay}`;
    }
  };

  const baseSelector = `.product-card__delivery-row[data-composition-id="${compositionId}"]`;
  const selector = isMade
    ? `${baseSelector}[data-made="true"]`
    : `${baseSelector}:not([data-made="true"])`;

  const $allContainers = document.querySelectorAll(selector);

  Array.from($allContainers).forEach($container => {
    renderDeliveryEstimates($container);
    if (isSameDay || isNextDay) {
      renderSameDayBadge($container, orderInTheNext, date);
    }
  });
};

const renderProductCardV3Estimates = ({ compositionId, date }) => {
  const selector = `.js-product-card-v3[data-composition-id="${compositionId}"]:not([data-is-featured="true"])`;

  const $allContainers = document.querySelectorAll(selector);

  $allContainers.forEach($container => {
    $container.querySelector(SELECTORS.DELIVERY_LOADING).classList.add("hidden");
    $container.querySelector(SELECTORS.DELIVERY_DATE).replaceChildren(date);
    $container.querySelector(SELECTORS.DELIVERY_LOADED).classList.remove("hidden");
    $container.querySelector(SELECTORS.DELIVERY_LOADED).classList.add("flex");
  });
};

const renderSameDayCompositions = rawCompositionIds => {
  const compositionIds = [...new Set(rawCompositionIds)];

  Array.from(compositionIds).forEach(compositionId => {
    const $sameDayContainer = document.querySelector(
      `.product-card__delivery-row[data-composition-id="${compositionId}"]`
    );
    const { sameDayCutoff: timeleft } = $sameDayContainer.dataset;
    renderEstimates({
      compositionId,
      date: SAME_DAY_DELIVERY_DATE,
      oneShipDay: true,
      orderInTheNext: timeleft,
      timeleft
    });
  });
};

export const loadCompositionEstimates = (rawCompositionIds, options) => {
  const compositionIds = [...new Set(rawCompositionIds)];
  const isMade = Boolean(options?.isMade);

  // Filter out composition IDs that are already being processed or have been sent
  const filteredCompositionIds = compositionIds.filter(compositionId =>
    shouldProcessCompositionId(compositionId, options)
  );

  if (filteredCompositionIds.length === 0) {
    return Promise.resolve([{}, 0]);
  }

  // Use individual requests only when isMade is true
  if (isMade) {
    // Send individual requests for each composition ID
    const promises = filteredCompositionIds.map(compositionId => {
      // Mark as processing
      markCompositionIdAsProcessing(compositionId, options);

      return fetcher
        .fetch([compositionId], options)
        .then(([data, ajaxTime]) => {
          if (data.success) {
            if (data.estimates[compositionId]) {
              const isFlash = Boolean(options?.isFlash);
              const isProductCardV3 = Boolean(options?.isProductCardV3);
              const isSameDay = data.estimates[compositionId].date.toLowerCase().includes("today");
              const {
                date,
                delivery_endtime: deliveryEndTime,
                delivery_type: deliveryType,
                inventory,
                one_ship_day: oneShipDay,
                shipping_rate: shippingRate,
                timeleft,
                delivery_day: deliveryDay
              } = data.estimates[compositionId] || {};

              if (options?.isSameDayEstimates) {
                renderSameDayEstimates(compositionId, timeleft, date);
              } else if (isProductCardV3) {
                renderProductCardV3Estimates({ compositionId, date });
              } else {
                renderEstimates({
                  compositionId,
                  date,
                  deliveryType,
                  inventory,
                  oneShipDay: isFlash || oneShipDay,
                  orderInTheNext: timeleft,
                  shippingRate,
                  timeleft: deliveryEndTime,
                  deliveryDay,
                  isMade
                });
              }
            } else {
              // Handle empty estimates - still need to hide spinners
              const isProductCardV3 = Boolean(options?.isProductCardV3);

              if (options?.isSameDayEstimates) {
                renderSameDayEstimates(compositionId, null, null);
              } else if (isProductCardV3) {
                renderProductCardV3Estimates({ compositionId, date: null });
              } else {
                renderEstimates({
                  compositionId,
                  date: null,
                  deliveryType: null,
                  inventory: null,
                  oneShipDay: false,
                  orderInTheNext: null,
                  shippingRate: null,
                  timeleft: null,
                  deliveryDay: null,
                  isMade
                });
              }
            }
          }

          // Mark as completed
          markCompositionIdAsCompleted(compositionId, options);
          return [data, ajaxTime];
        })
        .catch(error => {
          // Mark as completed even on error
          markCompositionIdAsCompleted(compositionId, options);
          throw error;
        });
    });

    // Return a promise that resolves when all requests complete
    return Promise.all(promises).then(
      results =>
        // Return the first result for backward compatibility
        results[0] || [{}, 0]
    );
  }
  // Use traditional batch strategy for non-made compositions
  // Mark all as processing
  filteredCompositionIds.forEach(compositionId => {
    markCompositionIdAsProcessing(compositionId, options);
  });

  return fetcher
    .fetch(filteredCompositionIds, options)
    .then(([data, ajaxTime]) => {
      if (data.success) {
        filteredCompositionIds.forEach(compositionId => {
          if (data.estimates[compositionId]) {
            const isFlash = Boolean(options?.isFlash);
            const isProductCardV3 = Boolean(options?.isProductCardV3);
            const isSameDay = data.estimates[compositionId].date.toLowerCase().includes("today");
            const {
              date,
              delivery_endtime: deliveryEndTime,
              delivery_type: deliveryType,
              inventory,
              one_ship_day: oneShipDay,
              shipping_rate: shippingRate,
              timeleft,
              delivery_day: deliveryDay
            } = data.estimates[compositionId] || {};

            if (options?.isSameDayEstimates) {
              renderSameDayEstimates(compositionId, timeleft, date);
            } else if (isProductCardV3) {
              renderProductCardV3Estimates({ compositionId, date });
            } else {
              renderEstimates({
                compositionId,
                date,
                deliveryType,
                inventory,
                oneShipDay: isFlash || oneShipDay,
                orderInTheNext: timeleft,
                shippingRate,
                timeleft: deliveryEndTime,
                deliveryDay,
                isMade
              });
            }
          } else {
            // Handle empty estimates - still need to hide spinners
            const isProductCardV3 = Boolean(options?.isProductCardV3);

            if (options?.isSameDayEstimates) {
              renderSameDayEstimates(compositionId, null, null);
            } else if (isProductCardV3) {
              renderProductCardV3Estimates({ compositionId, date: null });
            } else {
              renderEstimates({
                compositionId,
                date: null,
                deliveryType: null,
                inventory: null,
                oneShipDay: false,
                orderInTheNext: null,
                shippingRate: null,
                timeleft: null,
                deliveryDay: null,
                isMade
              });
            }
          }

          // Mark as completed
          markCompositionIdAsCompleted(compositionId, options);
        });
      }

      return [data, ajaxTime];
    })
    .catch(error => {
      // Mark all as completed even on error
      filteredCompositionIds.forEach(compositionId => {
        markCompositionIdAsCompleted(compositionId, options);
      });
      throw error;
    });
};

export const loadVisibleCompositionEstimates = () => {
  const renderStartTime = performance?.now() || 0;
  const estimatesRows = document.querySelectorAll(".js-plp-product-card-delivery");

  const visibleRows = Array.from(estimatesRows).filter(
    element => isElementVisible(element) && !isSpinnerHidden(element)
  );

  const { regularCompositionIds, madeCompositionIds, sameDayCutoffIds, flashCompositionIds } =
    getCompositionIds(visibleRows);

  let firstRequestCompleted = false;

  // Handle regular compositions with batch strategy
  if (regularCompositionIds.length) {
    loadCompositionEstimates(regularCompositionIds, { isMade: false, isFlash: false }).then(
      ([_data, ajaxTime]) => {
        if (!firstRequestCompleted) {
          firstRequestCompleted = true;
          const renderEndTime = performance?.now() || 0;
          trackDeliveryDatesRendered(renderEndTime - renderStartTime, ajaxTime, {
            render_trigger: "scroll"
          });
        }
      }
    );
  }

  // Handle made compositions with individual requests
  if (madeCompositionIds.length) {
    madeCompositionIds.forEach(compositionId => {
      loadCompositionEstimates([compositionId], { isMade: true, isFlash: false }).then(
        ([_data, ajaxTime]) => {
          if (!firstRequestCompleted) {
            firstRequestCompleted = true;
            const renderEndTime = performance?.now() || 0;
            trackDeliveryDatesRendered(renderEndTime - renderStartTime, ajaxTime, {
              render_trigger: "scroll"
            });
          }
        }
      );
    });
  }

  // Handle flash compositions with individual requests (since they are made)
  if (flashCompositionIds.length) {
    flashCompositionIds.forEach(compositionId => {
      loadCompositionEstimates([compositionId], { isMade: true, isFlash: true }).then(
        ([_data, ajaxTime]) => {
          if (!firstRequestCompleted) {
            firstRequestCompleted = true;
            const renderEndTime = performance?.now() || 0;
            trackDeliveryDatesRendered(renderEndTime - renderStartTime, ajaxTime, {
              render_trigger: "scroll"
            });
          }
        }
      );
    });
  }

  if (sameDayCutoffIds.length) {
    renderSameDayCompositions(sameDayCutoffIds);
  }
};

const getVisibleItemsCount = $estimatesRows => {
  const visibleItemsCount = Array.from($estimatesRows).filter($el => {
    if ($el.dataset.flash === "true") {
      const $flash = $el.closest(".js-product-card").previousElementSibling;
      return isElementVisible($flash);
    }
    return isElementVisible($el) && !isSpinnerHidden($el);
  }).length;
  return visibleItemsCount === 0 ? 2 : visibleItemsCount;
};

export const loadFirstFewRowsEstimates = () => {
  const $estimatesRows = document.querySelectorAll(".js-plp-product-card-delivery");

  const visibleItemsCount = getVisibleItemsCount($estimatesRows);

  // All with visible spinners but not necessarily visible in current viewport
  const $estimateableRows = Array.from($estimatesRows).filter($el => !isSpinnerHidden($el));

  // Get two packs of visibleItemsCount in a query that receives only 2 items at a time
  // For example, if we visibleItemsCount is 5 (desktop), we will get 5 queries with 2 items each
  // If visibleItemsCount is 2 (mobile), we will get 2 queries with 2 items each
  const $batches = chunkArray($estimateableRows, 2).slice(0, visibleItemsCount);

  let firstRequestCompleted = false;
  const renderStartTime = window?.jiffy?.timers?.productsRenderStartTime || 0;

  $batches.forEach($batch => {
    const { regularCompositionIds, madeCompositionIds, sameDayCutoffIds, flashCompositionIds } =
      getCompositionIds($batch);

    // Handle regular compositions with batch strategy
    if (regularCompositionIds.length) {
      loadCompositionEstimates(regularCompositionIds, { isMade: false, isFlash: false }).then(
        ([_data, ajaxTime]) => {
          if (!firstRequestCompleted) {
            firstRequestCompleted = true;
            const renderEndTime = performance?.now() || 0;
            trackDeliveryDatesRendered(renderEndTime - renderStartTime, ajaxTime, {
              render_trigger: "load"
            });
          }
        }
      );
    }

    // Handle made compositions with individual requests
    if (madeCompositionIds.length) {
      madeCompositionIds.forEach(compositionId => {
        loadCompositionEstimates([compositionId], { isMade: true, isFlash: false }).then(
          ([_data, ajaxTime]) => {
            if (!firstRequestCompleted) {
              firstRequestCompleted = true;
              const renderEndTime = performance?.now() || 0;
              trackDeliveryDatesRendered(renderEndTime - renderStartTime, ajaxTime, {
                render_trigger: "load"
              });
            }
          }
        );
      });
    }

    // Handle flash compositions with individual requests (since they are made)
    if (flashCompositionIds.length) {
      flashCompositionIds.forEach(compositionId => {
        loadCompositionEstimates([compositionId], { isMade: true, isFlash: true }).then(
          ([_data, ajaxTime]) => {
            if (!firstRequestCompleted) {
              firstRequestCompleted = true;
              const renderEndTime = performance?.now() || 0;
              trackDeliveryDatesRendered(renderEndTime - renderStartTime, ajaxTime, {
                render_trigger: "load"
              });
            }
          }
        );
      });
    }

    if (sameDayCutoffIds.length) {
      renderSameDayCompositions(sameDayCutoffIds);
    }
  });
};

export const initSameDayBadges = () => {
  const $elements = document.querySelectorAll(SELECTORS.SAME_DAY_BADGES);

  if ($elements.length === 0) {
    return;
  }

  const compositionIds = Array.from($elements).map(item => item.dataset.compositionId);
  loadCompositionEstimates(compositionIds, { isSameDayEstimates: true });
};
